import 'slick-carousel/slick/slick-theme.css';
import 'slick-carousel/slick/slick.css';
import '../Goal.scss';

import { useEffect, useState } from 'react';
import { GOAL_API_URL, GOAL_AUTH_TOKEN } from '../../../env';

import { useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useSearchParams } from 'react-router-dom';
import Slider from 'react-slick';
import appleStore from '../../../assets/images/download-apple-store.svg';
import googlePlay from '../../../assets/images/download-google-play.svg';
import espnGoal1 from '../../../assets/images/goal-alert-1.webp';
import espnGoal2 from '../../../assets/images/goal-alert-2.webp';
import espnGoal3 from '../../../assets/images/goal-alert-3.webp';
import espnGoal4 from '../../../assets/images/goal-alert-4.webp';
import espnGoal5 from '../../../assets/images/goal-alert-5.webp';
import espnGoal6 from '../../../assets/images/goal-alert-6.webp';
import topVisual from '../../../assets/images/goal-top.webp';
import Header from '../../../components/blocks/Header';
import Textblock from '../../../components/blocks/Textblock';
import PageLayout from '../../../layouts/PageLayout';
import { pageView } from '../../../scripts/analytics';
import AppStoreBanner from '../blocks/AppStoreBanner/AppStoreBanner';
import GoalHeader from '../blocks/GoalHeader/GoalHeader';
import QRCodeBlock from '../blocks/QRCodeBlock/QRCodeBlock';

const sliderSettings = {
    autoplay: true,
    dots: false,
    arrows: false,
    infinite: true,
    speed: 500,
    slidesToShow: 4,
    slidesToScroll: 1,
    responsive: [
        {
            breakpoint: 767,
            settings: {
                slidesToShow: 2,
            },
        },
        {
            breakpoint: 400,
            settings: {
                slidesToShow: 1,
            },
        },
    ],
};

const fetchGoalData = async (matchId, mediaId) => {
    const response = await fetch(
        `${GOAL_API_URL}/v2/media?matchId=${matchId}&mediaId=${mediaId}`,
        {
            method: 'GET',
            headers: {
                Authorization: GOAL_AUTH_TOKEN,
            },
        },
    );
    const res = await response.json();

    return res?.data;
};

const GoalVideo = () => {
    const [params] = useSearchParams();
    const [pageLoading, setPageLoading] = useState(true);
    const [goalApiData, setGoalApiData] = useState(null);

    useEffect(() => {
        pageView('goal');
    }, []);

    useEffect(() => {
        const matchId = params.get('matchId');
        const mediaId = params.get('mediaId');

        if (matchId && mediaId) {
            try {
                setPageLoading(true);
                fetchGoalData(matchId, mediaId).then(data => {
                    setGoalApiData(data);
                    setPageLoading(false);
                });
            } catch (error) {
                console.error(error);
            }
        } else {
            setPageLoading(false);
        }
    }, [params]);

    const metaContent = useMemo(() => {
        const { media } = goalApiData || {};

        const meta = {
            meta_title:
                media?.title || 'ESPN GOAL! – Alle goals in je broekzak',
            meta_description: 'Bekijk dit doelpunt direct!',
            og_title: media?.title || 'ESPN GOAL! – Alle goals in je broekzak',
            og_site_name: 'ESPN - GOAL!',
            og_locale: 'nl_NL',
            og_description: 'Bekijk dit doelpunt direct!',
            og_url: window.location.href,
            image:
                media?.thumbnail?.largeUrl ||
                `${window.location.origin}/img/goal-share-socials.png`,
            twitter_title:
                media?.title || 'ESPN GOAL! – Alle goals in je broekzak',
            twitter_description: 'Bekijk dit doelpunt direct!',
        };

        return meta;
    }, [goalApiData, window.location.href]);

    const goalHeaderContent = useMemo(() => {
        const { match, media } = goalApiData || {};
        return {
            date: match?.date
                ? new Intl.DateTimeFormat(['nl-NL', 'en-GB'], {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                  }).format(match?.date)
                : null,
            gameMinute: media?.minute,
            background: media?.thumbnail?.largeUrl,
            title: media?.title || metaContent?.meta_title,
            teams: match?.teams || {
                home: {
                    name: match?.homeTeam,
                },
                away: {
                    name: match?.awayTeam,
                },
            },
        };
    }, [metaContent, goalApiData]);

    return (
        <PageLayout id="goal" loading={pageLoading}>
            {metaContent && (
                <Helmet>
                    <title>{metaContent.meta_title}</title>
                    <meta
                        name="description"
                        property="description"
                        content={metaContent.meta_description}
                    />
                    <meta
                        property="og:title"
                        content={metaContent.og_title}
                        name="og:title"
                    />
                    <meta
                        property="og:site_name"
                        name="og:site_name"
                        content={metaContent.og_site_name}
                    />
                    <meta
                        property="og:description"
                        name="og:description"
                        content={metaContent.og_description}
                    />
                    <meta property="og:type" content="website" name="og:type" />
                    <meta
                        property="og:image"
                        content={metaContent.image}
                        name="og:image"
                    />

                    <meta
                        property="og:url"
                        content={metaContent.og_url}
                        name="og:url"
                    />
                    <meta
                        property="og:locale"
                        name="og:locale"
                        content={metaContent.og_locale}
                    />

                    <meta
                        property="twitter:title"
                        name="twitter:title"
                        content={metaContent.twitter_title}
                    />
                    <meta
                        property="twitter:description"
                        name="twitter:description"
                        content={metaContent.twitter_description}
                    />
                    <meta
                        property="twitter:image"
                        name="twitter:image"
                        content={metaContent.image}
                    />
                </Helmet>
            )}
            {goalApiData ? (
                <GoalHeader data={goalHeaderContent} />
            ) : (
                <Header
                    image={topVisual}
                    title={
                        <>
                            Alle goals in
                            <br />
                            je broekzak!
                        </>
                    }
                    buttons={[
                        {
                            label: (
                                <img
                                    height="40"
                                    src={appleStore}
                                    alt="Apple Store"
                                />
                            ),
                            link: 'https://apps.apple.com/nl/app/espn-goal/id6472048722',
                            target: 'blank',
                            track: 'compagne:goal:apple-store',
                        },
                        {
                            label: (
                                <img
                                    height="40"
                                    src={googlePlay}
                                    alt="Google Play"
                                />
                            ),
                            link: 'https://play.google.com/store/apps/details?id=com.espn.goalalert',
                            target: 'blank',
                            track: 'compagne:goal:google-play',
                        },
                    ]}
                    style={{
                        background: `center / cover url(${topVisual}) #ef4135`,
                    }}
                />
            )}

            <Textblock
                title="Wil je ook elke spits in je broekzak?"
                text={
                    <>
                        Download nu de ESPN Goal! app en mis geen enkel
                        doelpunt.
                    </>
                }
                links={[
                    {
                        label: 'Download in de app store',
                        url: 'https://apps.apple.com/nl/app/espn-goal/id6472048722',
                        target: 'blank',
                    },
                    {
                        label: 'Ontdek het op Google Play',
                        url: 'https://play.google.com/store/apps/details?id=com.espn.goalalert',
                        target: 'blank',
                    },
                ]}
            />
            <div className="container slider-container">
                <Slider {...sliderSettings}>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal1}
                            alt=""
                        />
                    </div>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal2}
                            alt=""
                        />
                    </div>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal3}
                            alt=""
                        />
                    </div>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal4}
                            alt=""
                        />
                    </div>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal5}
                            alt=""
                        />
                    </div>
                    <div>
                        <img
                            className="img-responsive"
                            src={espnGoal6}
                            alt=""
                        />
                    </div>
                </Slider>
            </div>
            {goalApiData && <QRCodeBlock />}
            <AppStoreBanner />
        </PageLayout>
    );
};

export default GoalVideo;
